import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { useSettings } from "@/providers/SettingsProvider";
import { getFormattedDateTime } from "@/utils/settings";

export interface JobDetails {
  id: string;
  title: string;
  company: {
    id: string;
    name: string;
  };
  contact?: {
    id: string;
    name: string;
    first_name?: string;
    last_name?: string;
    function?: string;
    email?: string;
    phone?: string;
  };
  description?: string;
  JobDesc_Public?: string;
  skills: string[];
  created_at: string;
  ai_analysis?: any; // Change to any to handle all possible JSON values
  ai_science_analysis?: any; // Add ai_science_analysis field for personality profiles
  contactId?: string;
  aiSummary?: string; // Add aiSummary field
  status?: string;
  // Fields
  employmentType?: string;
  contractDuration?: string;
  startDate?: string;
  endDate?: string;
  seniorityLevel?: string;
  budget?: string;
  budgetFreelance?: string;
  locationType?: string;
  workplaceBreakdown?: string;
  hardSkills?: string[];
  softSkills?: string[];
  educationLevel?: string;
  yearsOfExperience?: string;
  languageRequirements?: string;
  extraLang?: string;
  cultureTags?: string[];
  applicationDeadline?: string;
  jobPostingDate?: string;
  source?: string;
  assignedRecruiter?: {
    id: string;
    name: string;
    first_name?: string;
    last_name?: string;
  };
  employmentClassification?: string;
  published?: boolean;
  publishdate?: string | null;
  closing_reason?: string;
  custom_fields: any;
}

export const useJobDetail = (jobId: string | undefined) => {
  const [job, setJob] = useState<JobDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [formattedDate, setFormattedDate] = useState<string>("");
  const { dateFormat, timeFormat } = useSettings();

  const fetchJobDetail = async () => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Fetch the job details
      const { data, error } = await supabase
        .from('jobs')        
        .select(`
          id,
          title,
          description,
          "JobDesc_Public",
          skills,
          created_at,
          ai_analysis,
          ai_science_analysis,
          contact_id,
          company_id,
          companies(id, name),
          contacts(id, name, first_name, last_name, function, email, phone),
          employment_type,
          contract_duration,
          start_date,
          end_date,
          seniority_level,
          budget,
          budget_freelance,
          location_type,
          workplace_breakdown,
          hard_skills,
          soft_skills,
          education_level,
          years_of_experience,
          language_requirements,
          extra_lang,
          culture_tags,
          application_deadline,
          job_posting_date,
          source,
          assigned_recruiter,
          employment_classification,
          ai_summary,
          published,
          publishdate,
          closing_reason,
          custom_fields,
          status
        `)
        .eq('id', jobId)
        .single();

      // If we have an assigned recruiter, fetch their profile data
      let recruiterProfile = null;
      let recruiterId = data?.assigned_recruiter;
      
      //console.log('Raw assigned_recruiter value:', recruiterId, typeof recruiterId);
      
      // Handle case where assigned_recruiter might be stored as JSON object
      if (typeof recruiterId === 'string') {
        // Try to parse as JSON if it looks like a JSON string
        try {
          const parsed = JSON.parse(recruiterId);
          if (parsed && typeof parsed === 'object' && parsed.id) {
            recruiterId = parsed.id;
            //console.log('Extracted ID from JSON string:', recruiterId);
          }
        } catch (e) {
          // Not JSON, use as is
          //console.log('Using recruiterId as string:', recruiterId);
        }
      } else if (typeof recruiterId === 'object' && recruiterId !== null) {
        recruiterId = (recruiterId as any).id;
        //console.log('Extracted ID from object:', recruiterId);
      }
      
      if (recruiterId) {
        //console.log('Fetching profile for recruiter ID:', recruiterId);
        // Use the new database function that provides full access to user names
        const { data: profileData, error: profileError } = await supabase
          .rpc('get_user_display_info', {
            p_user_id: recruiterId
          });
        
        if (profileError) {
          console.error('Error fetching recruiter profile:', profileError);
        } else {
          //console.log('Recruiter profile data:', profileData);
        }
        
        // The function returns an array, so take the first item
        recruiterProfile = profileData && profileData.length > 0 ? profileData[0] : null;
      }



      if (error) {
        console.error("Error fetching job details:", error);
        setLoading(false);
        return;
      }

      // Format the job data
      const formattedJob: JobDetails = {
        id: data.id,
        title: data.title,        
        company: {
          id: data.company_id,
          name: (data.companies as any)?.name || 'Unknown Company'
        },
        contact: data.contact_id ? {
          id: data.contact_id,
          name: (data.contacts as any)?.name || '',
          first_name: (data.contacts as any)?.first_name,
          last_name: (data.contacts as any)?.last_name,
          function: (data.contacts as any)?.function,
          email: (data.contacts as any)?.email,
          phone: (data.contacts as any)?.phone
        } : undefined,
        description: data.description ?? '', // Ensure description is always a string
        JobDesc_Public: data.JobDesc_Public ?? '',
        skills: data.skills || [],
        created_at: data.created_at,
        ai_analysis: data.ai_analysis,
        ai_science_analysis: data.ai_science_analysis,
        aiSummary: data.ai_summary || '', // Include the ai_summary field
        contactId: data.contact_id,
        employmentType: data.employment_type,
        contractDuration: data.contract_duration,
        startDate: data.start_date,
        endDate: data.end_date || undefined,
        seniorityLevel: data.seniority_level,
        budget: data.budget,
        budgetFreelance: data.budget_freelance || undefined,
        locationType: data.location_type,
        workplaceBreakdown: data.workplace_breakdown,
        hardSkills: data.hard_skills || [],
        softSkills: data.soft_skills || [],
        educationLevel: data.education_level,
        yearsOfExperience: data.years_of_experience,
        languageRequirements: data.language_requirements,
        extraLang: data.extra_lang || undefined,
        cultureTags: data.culture_tags || [],
        applicationDeadline: data.application_deadline,
        jobPostingDate: data.job_posting_date,
        source: data.source,
        assignedRecruiter: recruiterId ? {
          id: recruiterId,
          name: recruiterProfile ? `${recruiterProfile.first_name || ''} ${recruiterProfile.last_name || ''}`.trim() : `User ${recruiterId}`,
          first_name: recruiterProfile?.first_name,
          last_name: recruiterProfile?.last_name
        } : undefined,
        employmentClassification: data.employment_classification,
        published: data.published,
        publishdate: data.publishdate,
        closing_reason: data.closing_reason,
        custom_fields: (data as any).custom_fields || {},
        status: data.status || 'Open',
      };



      setJob(formattedJob);

      // Format the date using user settings
      if (data.created_at) {
        try {
          // Keep the relative time format for the created_at date
          const date = new Date(data.created_at);
          
          // Check if date is valid before formatting
          if (!isNaN(date.getTime())) {
            const relativeTime = formatDistanceToNow(date, { addSuffix: true });
            setFormattedDate(relativeTime);
          } else {
            setFormattedDate("Invalid date");
          }
        } catch (err) {
          console.error("Error formatting date:", err);
          setFormattedDate("Date error");
        }
      }
      
    } catch (error) {
      console.error("Error fetching job details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobDetail();
  }, [jobId, dateFormat, timeFormat]);

  return {
    job,
    loading,
    formattedDate,
    refetch: fetchJobDetail
  };
};
